import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  CheckIcon, 
  XMarkIcon, 
  FolderIcon 
} from '@heroicons/react/24/outline';
import { useStore } from '../services/chatStore';
import ConfirmModal from './ConfirmModal';
import ThemeToggle from './ThemeToggle';
import { ChatCategory } from '../types/chat';
import CategoryMenu from './CategoryMenu';

export default function ChatSidebar() {
  const [searchText, setSearchText] = useState('');
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  
  const { chats, activeChat, createChat, setActiveChat, updateChatName, deleteChat, updateChatCategory } = useStore();
  const chatList = Object.values(chats);

  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    chatId: string | null;
    chatName: string;
  }>({
    isOpen: false,
    chatId: null,
    chatName: ''
  });

  const [selectedCategory, setSelectedCategory] = useState<ChatCategory>('All');

  const [categoryMenuState, setCategoryMenuState] = useState<{
    isOpen: boolean;
    chatId: string | null;
  }>({
    isOpen: false,
    chatId: null
  });

  const handleNewChat = () => {
    const newChat = createChat(`Chat ${Object.keys(chats).length + 1}`);
    setActiveChat(newChat.id);
  };

  const handleEditClick = (chatId: string, currentName: string) => {
    setEditingChatId(chatId);
    setEditName(currentName);
  };

  const handleEditSubmit = (chatId: string) => {
    if (editName.trim()) {
      updateChatName(chatId, editName.trim());
    }
    setEditingChatId(null);
  };

  const handleDeleteClick = (chatId: string, chatName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteModalState({
      isOpen: true,
      chatId,
      chatName
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalState.chatId) {
      deleteChat(deleteModalState.chatId);
    }
    setDeleteModalState({
      isOpen: false,
      chatId: null,
      chatName: ''
    });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({
      isOpen: false,
      chatId: null,
      chatName: ''
    });
  };

  useEffect(() => {
    if (editingChatId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingChatId]);

  const handleChatSelect = useCallback((chatId: string) => {
    setActiveChat(chatId);
  }, [setActiveChat]);

  const filteredChats = chatList.filter(chat => 
    (selectedCategory === 'All' || chat.category === selectedCategory) &&
    chat.name.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <>
      <div className="w-80 flex flex-col h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
        {/* Fixed Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-xl font-semibold flex-1 text-gray-900 dark:text-white">Chats</h2>
            <ThemeToggle />
            <button 
              onClick={handleNewChat}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <PlusIcon className="h-6 w-6 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
          
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search chats"
              className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-full focus:outline-none"
            />
          </div>
        </div>

        {/* Category Selector */}
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value as ChatCategory)}
            className="w-full bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="All">All Chats</option>
            <option value="Work">Work</option>
            <option value="Personal">Personal</option>
            <option value="Important">Important</option>
          </select>
        </div>

        {/* Scrollable Chat List */}
        <div className="flex-1 overflow-hidden hover:overflow-y-auto custom-scrollbar">
          <div className="p-2">
            {filteredChats.map((chat) => (
              <div key={chat.id} className="relative group">
                <div
                  onClick={() => !editingChatId && handleChatSelect(chat.id)}
                  className={`group flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                    activeChat === chat.id ? 'bg-gray-100 dark:bg-gray-700' : ''
                  }`}
                >
                  <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                    {chat.name.charAt(0)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    {editingChatId === chat.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          ref={editInputRef}
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="flex-1 px-2 py-1 border rounded focus:outline-none focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleEditSubmit(chat.id);
                            if (e.key === 'Escape') setEditingChatId(null);
                          }}
                        />
                        <button
                          onClick={() => handleEditSubmit(chat.id)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                        >
                          <CheckIcon className="w-4 h-4 text-green-600" />
                        </button>
                        <button
                          onClick={() => setEditingChatId(null)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                        >
                          <XMarkIcon className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900 dark:text-white truncate">
                            {chat.name}
                          </h3>
                          {chat.category && chat.category !== 'All' && (
                            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                              {chat.category}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {chat.lastMessage || 'No messages yet'}
                        </p>
                      </>
                    )}
                  </div>

                  {!editingChatId && (
                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(chat.id, chat.name);
                        }}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                      >
                        <PencilIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCategoryMenuState({ isOpen: true, chatId: chat.id });
                        }}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                      >
                        <FolderIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteClick(chat.id, chat.name, e)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                      >
                        <TrashIcon className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  )}

                  <div className="text-xs text-gray-400 flex-shrink-0">
                    {new Date(chat.timestamp).toLocaleDateString()}
                  </div>
                </div>

                {/* Category Menu */}
                {categoryMenuState.isOpen && categoryMenuState.chatId === chat.id && (
                  <CategoryMenu
                    isOpen={true}
                    onClose={() => setCategoryMenuState({ isOpen: false, chatId: null })}
                    onSelect={(category) => updateChatCategory(chat.id, category)}
                    currentCategory={chat.category}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Credit Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            <span>Made with ❤️ by </span>
            <span className="ml-1 font-medium text-blue-600 dark:text-blue-400">
              Zerotracex
            </span>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        title="Delete Chat"
        message={`Are you sure you want to delete "${deleteModalState.chatName}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
} 